/**
 * Translation Service - Frontend
 * Handles exercise translation with multi-level caching
 */

const CACHE_EXPIRY_DAYS = 30;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Get translated exercise content with smart caching
 * Cache levels:
 * 1. Memory cache (fast, session-only)
 * 2. localStorage (persistent, cross-session)
 * 3. Backend API (with MongoDB cache)
 * 
 * @param {string} exerciseId - Exercise ID
 * @param {string} targetLanguage - Target language code (es, fr, de, etc.)
 * @param {object} originalExercise - Original exercise data (fallback)
 * @returns {Promise<object>} Translated exercise content
 */

// Memory cache (fast access during session)
const memoryCache = {};

export const getTranslatedExercise = async (exerciseId, targetLanguage, originalExercise) => {
  // If English, return original
  if (targetLanguage === 'en') {
    return originalExercise;
  }

  const cacheKey = `ex_${exerciseId}_${targetLanguage}`;

  // Level 1: Check memory cache
  if (memoryCache[cacheKey]) {
    console.log(`✅ Translation memory cache hit: ${exerciseId} -> ${targetLanguage}`);
    return memoryCache[cacheKey];
  }

  // Level 2: Check localStorage
  try {
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      const parsed = JSON.parse(cached);
      const expiryDate = new Date(parsed.timestamp);
      expiryDate.setDate(expiryDate.getDate() + CACHE_EXPIRY_DAYS);

      if (new Date() < expiryDate) {
        console.log(`✅ Translation localStorage hit: ${exerciseId} -> ${targetLanguage}`);
        // Store in memory for faster access
        memoryCache[cacheKey] = parsed.content;
        return parsed.content;
      } else {
        // Expired, remove from localStorage
        localStorage.removeItem(cacheKey);
      }
    }
  } catch (e) {
    console.warn('localStorage read error:', e);
  }

  // Level 3: Fetch from backend API (which has MongoDB cache)
  try {
    console.log(`🤖 Fetching translation: ${exerciseId} -> ${targetLanguage}`);
    
    const response = await fetch(
      `${BACKEND_URL}/api/exercises/${exerciseId}/translate`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          target_language: targetLanguage
        })
      }
    );

    if (!response.ok) {
      throw new Error(`Translation API error: ${response.status}`);
    }

    const data = await response.json();
    const translatedContent = data.content;

    // Cache in localStorage
    try {
      localStorage.setItem(cacheKey, JSON.stringify({
        content: translatedContent,
        timestamp: new Date().toISOString()
      }));
    } catch (e) {
      console.warn('localStorage write error:', e);
    }

    // Cache in memory
    memoryCache[cacheKey] = translatedContent;

    console.log(`✅ Translation fetched and cached: ${exerciseId} -> ${targetLanguage}`);
    return translatedContent;

  } catch (error) {
    console.error('Translation error:', error);
    // Fallback to original content
    console.log('⚠️ Falling back to original content');
    return originalExercise;
  }
};

/**
 * Clear translation cache for a specific exercise or all
 * @param {string} exerciseId - Optional: specific exercise ID
 * @param {string} language - Optional: specific language
 */
export const clearTranslationCache = (exerciseId = null, language = null) => {
  if (exerciseId && language) {
    // Clear specific translation
    const cacheKey = `ex_${exerciseId}_${language}`;
    delete memoryCache[cacheKey];
    try {
      localStorage.removeItem(cacheKey);
    } catch (e) {
      console.warn('localStorage remove error:', e);
    }
  } else if (exerciseId) {
    // Clear all translations for an exercise
    Object.keys(memoryCache).forEach(key => {
      if (key.startsWith(`ex_${exerciseId}_`)) {
        delete memoryCache[key];
      }
    });
    try {
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith(`ex_${exerciseId}_`)) {
          localStorage.removeItem(key);
        }
      });
    } catch (e) {
      console.warn('localStorage clear error:', e);
    }
  } else {
    // Clear all translations
    Object.keys(memoryCache).forEach(key => {
      if (key.startsWith('ex_')) {
        delete memoryCache[key];
      }
    });
    try {
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('ex_')) {
          localStorage.removeItem(key);
        }
      });
    } catch (e) {
      console.warn('localStorage clear error:', e);
    }
  }
};

/**
 * Preload translations for common exercises
 * @param {array} exerciseIds - Array of exercise IDs to preload
 * @param {string} targetLanguage - Target language code
 */
export const preloadTranslations = async (exerciseIds, targetLanguage) => {
  if (targetLanguage === 'en') return;

  console.log(`🔄 Preloading ${exerciseIds.length} translations for ${targetLanguage}`);
  
  const promises = exerciseIds.slice(0, 10).map(async (exerciseId) => {
    try {
      await getTranslatedExercise(exerciseId, targetLanguage, {});
    } catch (e) {
      console.warn(`Preload failed for ${exerciseId}:`, e);
    }
  });

  await Promise.allSettled(promises);
  console.log(`✅ Preload complete for ${targetLanguage}`);
};

/**
 * Supported languages
 */
export const SUPPORTED_LANGUAGES = {
  en: 'English',
  es: 'Español',
  fr: 'Français',
  de: 'Deutsch',
  it: 'Italiano',
  pt: 'Português',
  nl: 'Nederlands',
  pl: 'Polski',
  ru: 'Русский',
  ja: '日本語',
  zh: '中文',
  ar: 'العربية'
};
